import { useState } from "react";
import { Link } from "react-router-dom";
import { BASE_URL } from '../apiConfig';
import { useAuth } from "../context/AuthProvider";
import { toast } from 'sonner';
import DeleteModal from '../cors/DeleteModal'; 
import { ShieldCheck, UserRoundCog } from 'lucide-react'

import { useLanguage } from "../context/LanguageProvider";

export default function AdminList({ admin , fetchUsers}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { token } = useAuth();
  const { t } = useLanguage();

  const deleteAdmin = async () => {
    setIsModalOpen(false); 

    try {
      const res = await fetch(BASE_URL+`/api/users/${admin.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      if (res.ok) {
        toast.success("Suppression réussie");
        fetchUsers();
      } else {
        const err = await res.json()
        toast.error("Echec de suppression");
      }
    } catch (error) {
      toast.error("Echec de suppression");
    }
  }
  const formatDate = (dateString) => {
    if (!dateString || dateString === "") return t('admins.never');
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return t('admins.not_provided');
    const phoneStr = phone.toString();
    return phoneStr.replace(/(\d{2})(\d{3})(\d{4})/, "($1) $2-$3");
  };

  const getActivityStatus = () => {
    if (!admin.info.last_login_at || admin.info.last_login_at === "") {
      return { 
        status: t('admins.never_connected'), 
        color: "text-red-600 bg-red-100",
        bg: "bg-red-600",
        tooltip: t('admins.tooltip_never_connected')
      };
    }
    
    const lastLogin = new Date(admin.info.last_login_at);
    const now = new Date();
    const diffInSeconds = Math.floor((now - lastLogin) / 1000);
    const minutesPassed = Math.floor(diffInSeconds / 60);
    const hoursPassed = Math.floor(minutesPassed / 60);
    const daysPassed = Math.floor(hoursPassed / 24);

    if (diffInSeconds < 60) {
      return {
        status: t('admins.online_now'),
        color: "text-green-600 bg-green-100",
        bg: "bg-green-600",
        tooltip: `${t('admins.tooltip_online_now')} (${lastLogin.toLocaleString()})`
      };
    }
    if (minutesPassed < 60) {
      return {
        status: t('admins.online_min', { minutes: minutesPassed }),
        color: "text-green-600 bg-green-100",
        bg: "bg-green-600",
        tooltip: t('admins.tooltip_min_ago', { minutes: minutesPassed })
      };
    }
    if (hoursPassed < 24) {
      return {
        status: t('admins.online_hours', { hours: hoursPassed }),
        color: "text-blue-600 bg-blue-100",
        bg: "bg-blue-600",
        tooltip: t('admins.tooltip_hours_ago', { hours: hoursPassed })
      };
    }
    if (daysPassed === 1) {
      return {
        status: t('admins.online_day'),
        color: "text-yellow-600 bg-yellow-100",
        bg: "bg-yellow-600",
        tooltip: `${t('admins.tooltip_yesterday')} (${lastLogin.toLocaleString()})`
      };
    }
    if (daysPassed < 7) {
      return {
        status: t('admins.online_days', { days: daysPassed }),
        color: "text-amber-600 bg-amber-100",
        bg: "bg-amber-600",
        tooltip: t('admins.tooltip_days_ago', { days: daysPassed })
      };
    }
    if (daysPassed < 30) {
      const weeksPassed = Math.floor(daysPassed / 7);
      return {
        status: t('admins.online_weeks', { weeks: weeksPassed }),
        color: "text-orange-600 bg-orange-100",
        bg: "bg-orange-600",
        tooltip: t('admins.tooltip_weeks_ago', { weeks: weeksPassed })
      };
    }
    return {
      status: t('admins.inactive'),
      color: "text-red-600 bg-red-100",
      bg: "bg-red-600",
      tooltip: `${t('admins.tooltip_last_activity_month')} (${lastLogin.toLocaleDateString()})`
    };
  };

  const activity = getActivityStatus();
  const userType = admin.type === 1 ? t('admins.user_type_admin') : t('admins.user_type_assistant');

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-6 py-4 hover:shadow-xl transition-all duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* User Info */}
        <div className="flex items-center gap-4">
          {
            userType === "Admin" ?
            <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-2xl flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-[var(--color-primary-500)]" />
            </div>
            :
            <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
              <UserRoundCog  className="h-6 w-6 text-orange-600" />
            </div>
          }
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-gray-900">{admin.info?.name || admin.email}</h3>
              <span className={`px-2 py-0.5 rounded-full font-semibold text-[11px] ${activity.color}`}>
                {activity.status}
              </span>
            </div>
            <p className="text-gray-600 text-xs">{admin.email}</p>
            <div className="flex items-center gap-3 mt-1">
              {userType === "Admin" ? 
                <span className="text-[11px] bg-green-200  rounded-full pt-0.2 pb-0.5 px-2 text-green-600">{userType}</span> 
                :
                <span className="text-[11px] bg-orange-200 0 rounded-full pt-0.2 pb-0.5 px-2 text-orange-600">{userType}</span>
              }
              {admin.type === 2 && admin.roles && admin.roles.length > 0 && (
                <span className="text-xs bg-blue-100  text-blue-800 px-2 py-0.5 rounded-full">
                  {admin.roles[0].name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-500 text-xs">{t('admins.phone_label')}</p>
            <p className="font-medium text-xs">{formatPhone(admin.info?.phone)}</p>
          </div>
          <div>

            <p className="font-medium text-xs">{t('admins.last_login_label')}</p>
            <p className="font-medium text-xs">{formatDate(admin.info?.last_login_at)}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">{t('admins.created_label')}</p>
            <p className="font-medium text-xs">{formatDate(admin.created_at)}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Link to={`/admins/${admin.id}`}>
            <button className="p-2  cursor-pointer border-2 border-gray-200 hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)] rounded-xl transition-all duration-300">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          </Link>
          <button 
            onClick={() => setIsModalOpen(true)} 
            className="p-2 border-2 border-gray-200 cursor-pointer hover:border-red-400 text-gray-600 hover:text-red-500 rounded-xl transition-all duration-300"
            title="Voir les détails"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2
                2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1
                1 0 00-1-1h-4a1 1 0 00-1 1v3m5 0H6"
              />
            </svg>
          </button>
        </div>
      </div>
      {
        isModalOpen && 
          <DeleteModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={deleteAdmin}
            title={`Supprimer l'${userType}`}
            message={`Êtes-vous sûr de vouloir supprimer l'${userType}`}
            itemName={admin.info.name}
          />
      }
    </div>
  );
}